import Badge from '@material-ui/core/Badge';
import useLearningObjectives from 'core/hooks/learningObjectives/useLearningObjectives';
import TreeView from 'core/modules/components/TreeViewSelectable/TreeView';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, Tag } from 'ui';
import TextLink from '../../../atoms/TextLink';
import './_select-learning-objectives.scss';
import { getObjectivesWithRelatives } from './getObjectivesWithRelatives';

function SelectLearningObjectives({
  buttonTextTranslationKey = 'select_learning_objectives',
  onChange = () => null,
  onOpen = () => null,
  pillButton = false,
  placeholder = '',
  shouldOpenSelected = false,
  showBadge = true,
  value = [],
  variant = 'button',
}) {
  const [data, setData] = useState([]);
  const [open, setOpen] = useState(false);
  const [tabToOpen, setTabToOpen] = useState('everything');
  const [selected, setSelected] = useState(value);
  const { t } = useTranslation(['contents', 'common', 'lesson']);

  const { listOfAllLearningObjectives } = useLearningObjectives({ selectedObjectives: [] });

  function getData(params) {
    let tempList = listOfAllLearningObjectives.filter(
      (item) =>
        item.learning_objective.toLowerCase().indexOf(params.search.toLowerCase()) > -1 ||
        item.description.toLowerCase().indexOf(params.search.toLowerCase()) > -1
    );
    setData(getObjectivesWithRelatives(listOfAllLearningObjectives, tempList));
  }

  function onSelected(event) {
    onChange(event);
    setSelected(event);
  }

  function handleRemoveTag(e, guidToRemove) {
    e.stopPropagation();
    const updated = selected.filter((item) => item.guid !== guidToRemove);
    onChange(updated);
    setSelected(updated);
  }

  function handleInputClick() {
    setTabToOpen('everything');
    setOpen(true);
  }

  function onClose(event) {
    setOpen(event);
    setTabToOpen('everything');
  }

  function onClick(event) {
    event.preventDefault();
    setOpen(true);
  }

  useEffect(() => {
    const dataList = listOfAllLearningObjectives.map((item) => ({ ...item, name: item.learning_objective, value: item.guid }));
    setData(dataList);
  }, [listOfAllLearningObjectives]);

  useEffect(() => {
    setSelected(value);
  }, [value]);

  useEffect(() => {
    if (shouldOpenSelected) {
      setTabToOpen('selected');
      setOpen(true);
      onOpen();
    }
  }, [shouldOpenSelected]);

  return (
    <>
      {variant === 'button' && (
        <div className="select-learning-objectives__button">
          {!pillButton && <Button text={t(buttonTextTranslationKey)} onClick={onClick} variant="neutral-soft" leftIcon="add-circle" />}
          {pillButton && (
            <div className="learning-objectives-tag-list-and-create__title">
              <TextLink text={t('actions:Add')} onClick={onClick} icon="add-circle" />
            </div>
          )}
          {showBadge && <Badge badgeContent={selected.length} color="primary" />}
        </div>
      )}
      {variant === 'input' && (
        <div
          className="select-learning-objectives__input-container"
          onClick={handleInputClick}
          onKeyDown={(e) => e.key === 'Enter' && handleInputClick()}
          role="button"
          tabIndex={0}
        >
          <div className="select-learning-objectives__tags">
            {selected.length > 0 ? (
              selected.map((item) => <Tag key={item.guid} label={item.learning_objective} onClick={(e) => handleRemoveTag(e, item.guid)} rightIcon="close" />)
            ) : (
              <span className="select-learning-objectives__placeholder">{placeholder}</span>
            )}
          </div>
          <div className="select-learning-objectives__chevron">
            <Icon color="grey-01" size="sm" name="chevron-down" />
          </div>
        </div>
      )}
      <TreeView
        data={data}
        getData={getData}
        onClose={onClose}
        onSelected={onSelected}
        open={open}
        tabToOpen={tabToOpen}
        titleDialog={t('content:Select Learning Objectives Dialog')}
        titleName="learning_objective"
        value={selected}
      />
    </>
  );
}

SelectLearningObjectives.propTypes = {
  buttonTextTranslationKey: PropTypes.string,
  onChange: PropTypes.func,
  onOpen: PropTypes.func,
  pillButton: PropTypes.bool,
  placeholder: PropTypes.string,
  shouldOpenSelected: PropTypes.bool,
  showBadge: PropTypes.bool,
  value: PropTypes.array,
  variant: PropTypes.oneOf(['button', 'input']),
};

export default SelectLearningObjectives;
